import { Entity } from '@mainstage/catalog-model';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { usePracticeShaperGraphData } from '../hooks/practice-shaper-graph/usePracticeShaperGraphData';

export type PracticeShaperGraphProps = {
  data: ReturnType<typeof usePracticeShaperGraphData>;
  filteredRelations: string[];
  setFilteredRelations: Dispatch<SetStateAction<string[]>>;
  selectedEntity: Entity | null;
  setSelectedEntity: Dispatch<SetStateAction<Entity | null>>;
  selectedTaxonomyRefs: string[];
  setSelectedTaxonomyRefs: Dispatch<SetStateAction<string[]>>;
};

export const PracticeShaperGraphContext =
  createContext<PracticeShaperGraphProps>({} as PracticeShaperGraphProps);

interface Props {
  children?: ReactNode;
}

const DEFAULT_EXCLUDED_RELATIONS = [
  'hasPart',
  'hasChild',
  'readBy',
  'extendedBy',
  'evolvedBy',
];

export const HIGHLIGHTED_RELATIONS = ['partOf', 'belongsTo'];

const LANDSCAPE_FILTER_KEY = 'practiceShaperGraphLandscapeFilter';

const getInitialTaxonomyRefs = (): string[] => {
  try {
    const stored = localStorage.getItem(LANDSCAPE_FILTER_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const PracticeShaperGraphProvider: FC<Props> = ({ children }) => {
  const data = usePracticeShaperGraphData();
  const [filteredRelations, setFilteredRelations] = useState(
    DEFAULT_EXCLUDED_RELATIONS,
  );

  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);

  const [selectedTaxonomyRefs, setSelectedTaxonomyRefsState] = useState<
    string[]
  >(getInitialTaxonomyRefs);

  const setSelectedTaxonomyRefs: Dispatch<SetStateAction<string[]>> =
    useCallback(value => {
      setSelectedTaxonomyRefsState(prev => {
        const newValue = typeof value === 'function' ? value(prev) : value;
        localStorage.setItem(LANDSCAPE_FILTER_KEY, JSON.stringify(newValue));
        return newValue;
      });
    }, []);

  return (
    <PracticeShaperGraphContext.Provider
      value={{
        data,
        filteredRelations,
        setFilteredRelations,
        selectedEntity,
        setSelectedEntity,
        selectedTaxonomyRefs,
        setSelectedTaxonomyRefs,
      }}
    >
      {children}
    </PracticeShaperGraphContext.Provider>
  );
};

export const usePracticeShaperGraph = () =>
  useContext(PracticeShaperGraphContext);
